/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useState, useEffect } from 'react';
import { X, HelpCircle } from 'lucide-react';
import YouTubePlayer from './YouTubePlayer';
import ChordTimeline from './ChordTimeline';
import Fretboard from '../Fretboard';
import FretRangeSelector from '../FretRangeSelector';
import { BackingTrack, TimelineEntry } from '../../types/jamTrack';
import { fetchChordNotes, fetchScaleNotes } from '../../api';
import { Button } from '../ui/button';
import '../../styles/jamtrack.css';

interface JamTrackProps {
  track: BackingTrack;
  currentKey: string;
  currentScale: string;
  selectedTuning: string[];
  fretRange: { start: number; end: number };
  totalFrets: number;
  isVertical: boolean;
  onFretRangeChange: (range: { start: number; end: number }) => void;
  onExit: () => void;
}

const JamTrack: React.FC<JamTrackProps> = ({
  track,
  currentKey,
  currentScale,
  selectedTuning,
  fretRange,
  totalFrets,
  isVertical,
  onFretRangeChange,
  onExit
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const [currentChord, setCurrentChord] = useState<TimelineEntry | null>(null);
  const [currentChordNotes, setCurrentChordNotes] = useState<string[]>([]);
  const [scaleNotes, setScaleNotes] = useState<any>(null);
  const [chordNotesCache, setChordNotesCache] = useState<Record<string, string[]>>({});
  const [showChordToneTargetingHint, setShowChordToneTargetingHint] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Helper function to normalize enharmonic equivalents
  const normalizeEnharmonics = (noteNames: string[]): string[] => {
    const enharmonicMap: Record<string, string> = {
      'Gb': 'F#',
      'Ab': 'G#', 
      'Bb': 'A#',
      'Db': 'C#',
      'Eb': 'D#',
      'Cb': 'B',
      'Fb': 'E',
      'E#': 'F',
      'B#': 'C'
    };
    
    return noteNames.map(note => enharmonicMap[note] || note);
  };

  // Helper function to parse chord notation
  const parseChord = (chordSymbol: string) => {
    const chordRoot = chordSymbol.charAt(0) + (chordSymbol.charAt(1) === '#' || chordSymbol.charAt(1) === 'b' ? chordSymbol.charAt(1) : '');
    const chordSuffix = chordSymbol.substring(chordRoot.length);
    
    console.log(`🔍 parseChord DEBUG: "${chordSymbol}" -> root: "${chordRoot}", suffix: "${chordSuffix}"`);
    
    // Map chord suffixes to backend expected values
    let chordQuality = 'major'; // default
    if (chordSuffix === 'm' || chordSuffix === 'min') {
      chordQuality = 'minor';
    } else if (chordSuffix === '7') {
      chordQuality = 'dominant7';
    } else if (chordSuffix === 'maj7' || chordSuffix === 'M7') {
      chordQuality = 'major7';
    } else if (chordSuffix === 'm7') {
      chordQuality = 'minor7';
    } else if (chordSuffix === 'dim') {
      chordQuality = 'diminished';
    } else if (chordSuffix === 'aug') {
      chordQuality = 'augmented';
    } else if (chordSuffix === 'sus4') {
      chordQuality = 'sus4';
    } else if (chordSuffix === 'sus2') {
      chordQuality = 'sus2';
    }
    
    console.log(`🔍 parseChord RESULT: root: "${chordRoot}", quality: "${chordQuality}"`);
    return { root: chordRoot, quality: chordQuality };
  };

  // Pre-fetch all chord data when timeline loads
  useEffect(() => {
    const preFetchChordData = async () => {
      if (!track.timeline || track.timeline.length === 0) return;

      // Extract unique chords from timeline
      const uniqueChords = Array.from(new Set(track.timeline.map(entry => entry.chord)))
        .filter(chord => chord && chord.trim() !== '');

      console.log('🎵 PRE-FETCHING chord data for:', uniqueChords);

      const cache: Record<string, string[]> = {};
      
      // Fetch chord notes for all unique chords
      for (const chordSymbol of uniqueChords) {
        try {
          const { root, quality } = parseChord(chordSymbol);
          console.log(`🔍 PARSING ${chordSymbol}:`, { root, quality });
          
          const chordNotes = await fetchChordNotes(root, quality);
          console.log(`🔍 RAW API RESPONSE for ${chordSymbol}:`, chordNotes);
          
          if (chordNotes && chordNotes.notesAndIntervals && Array.isArray(chordNotes.notesAndIntervals)) {
            const rawNoteNames = chordNotes.notesAndIntervals.map((item: any) => item.note.note);
            const normalizedNoteNames = normalizeEnharmonics(rawNoteNames);
            cache[chordSymbol] = normalizedNoteNames;
            console.log(`🎵 RAW NOTES for ${chordSymbol}:`, rawNoteNames);
            console.log(`🎵 NORMALIZED & CACHED ${chordSymbol}:`, normalizedNoteNames);
          } else {
            console.warn(`Invalid chord notes format for ${chordSymbol}:`, chordNotes);
            cache[chordSymbol] = [];
          }
        } catch (error) {
          console.error(`Error pre-fetching chord notes for ${chordSymbol}:`, error);
          cache[chordSymbol] = [];
        }
      }

      setChordNotesCache(cache);
      console.log('🎵 CHORD CACHE READY:', Object.keys(cache));
    };

    preFetchChordData();
  }, [track.timeline]);

  // Fetch scale notes when component mounts or key/scale changes
  useEffect(() => {
    const fetchScale = async () => {
      try {
        const notes = await fetchScaleNotes(currentKey, currentScale);
        setScaleNotes(notes);
      } catch (error) {
        console.error('Error fetching scale notes:', error);
        setScaleNotes(null);
      }
    };

    fetchScale();
  }, [currentKey, currentScale]);

  // Handle time updates from YouTube player
  const handleTimeUpdate = (time: number, chord: TimelineEntry | null) => {
    setCurrentTime(time);
    setCurrentChord(chord);
  };

  // Update chord notes when current chord changes (using cached data)
  useEffect(() => {
    if (!currentChord?.chord) {
      setCurrentChordNotes([]);
      return;
    }

    // Use cached chord notes instead of making API calls
    const cachedNotes = chordNotesCache[currentChord.chord];
    
    if (cachedNotes) {
      console.log('🎵 USING CACHED CHORD NOTES for', currentChord.chord, ':', cachedNotes);
      setCurrentChordNotes(cachedNotes);
    } else {
      console.warn('🎵 CHORD NOT IN CACHE:', currentChord.chord);
      // Fallback: chord not in cache (shouldn't happen if pre-fetch worked)
      setCurrentChordNotes([]);
    }
  }, [currentChord, chordNotesCache]);

  // Handle chord click from timeline
  const handleChordClick = (chord: TimelineEntry) => {
    // This would typically seek the YouTube player to the chord's start time
    // Implementation depends on the YouTube player ref access
    console.log('Jumping to chord:', chord);
  };

  if (!track) {
    return <div>Loading track...</div>;
  }

  if (!track.youTubeVideoId) {
    return (
      <div className="jam-track-layout">
        <button onClick={onExit} className="exit-playback-btn">
          <X className="h-4 w-4" />
          Back to App
        </button>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <h2 className="text-xl font-semibold mb-2">No Video Available</h2>
            <p className="text-muted-foreground">This track doesn't have a valid YouTube video ID.</p>
            <p className="text-sm text-muted-foreground mt-2">Video ID: {track.youTubeVideoId || 'Missing'}</p>
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className={`jam-track-layout ${isVertical ? 'jam-track-vertical' : 'jam-track-horizontal'}`}>
      {/* Exit Button */}
      <button
        onClick={onExit}
        className="exit-playback-btn"
        aria-label="Return to main app"
      >
        <X className="h-4 w-4" />
        Back to App
      </button>

      {/* YouTube Video Player */}
      <div className="jam-track-video">
        <YouTubePlayer
          videoId={track.youTubeVideoId}
          timeline={track.timeline}
          onTimeUpdate={handleTimeUpdate}
          onPlayerReady={() => console.log('YouTube player ready')}
          compact={isMobile || !isVertical}
        />
      </div>

      {/* Fret range controls - Mobile (above fretboard) */}
      <div className="jam-track-fret-range lg:hidden">
        <FretRangeSelector
          fretRange={fretRange}
          totalFrets={totalFrets}
          onRangeChange={onFretRangeChange}
          isMobile={true}
        />
      </div>

      {/* Fretboard with Chord Highlighting */}
      <div className="jam-track-fretboard relative">
        {/* Fret range controls - Desktop (overlay on fretboard) */}
        <div className="hidden lg:block absolute top-4 left-4 z-20 w-64 max-w-full pointer-events-none">
          <div className="pointer-events-auto">
            <FretRangeSelector
              fretRange={fretRange}
              totalFrets={totalFrets}
              onRangeChange={onFretRangeChange}
            />
          </div>
        </div>
        <Fretboard
          noteCollection={scaleNotes}
          isVertical={isMobile || isVertical}
          strings={selectedTuning.length}
          frets={totalFrets}
          tuning={selectedTuning}
          showNoteNames={true}
          fretRange={fretRange}
          jamTrackMode={true}
          currentChordNotes={currentChordNotes}
        />
      </div>

      {/* Chord Tone Targeting Tips Sidebar */}
      <div className="jam-track-tips">
        <div className="relative">
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-8 w-8 p-0 bg-black/20 hover:bg-black/30 text-white border border-white/20 backdrop-blur-sm"
            onClick={() => setShowChordToneTargetingHint(!showChordToneTargetingHint)}
            aria-label="Chord tone targeting tips"
          >
            <HelpCircle className="h-4 w-4" />
          </Button>
          
          {/* Hint Content */}
          {showChordToneTargetingHint && (
            <div className="absolute right-0 top-0 w-80 p-4 bg-slate-900 text-white border border-slate-700 rounded-lg shadow-xl z-20 backdrop-blur-sm">
              <div className="space-y-3">
                <h4 className="font-semibold text-amber-400 mb-2">🎸 Chord Tone Targeting Tips</h4>
                
                <p className="text-sm text-slate-300">
                  <strong>Chord tone targeting</strong> means playing the notes that make up each chord as it changes. This creates strong, melodic lines that sound "right" over any progression.
                </p>

                <p className="text-sm text-slate-300">
                  If the neck feels crowded, tighten your focus to a single position (about seven frets or fewer) so your hands stay relaxed and you can react to each chord change without scrambling.
                </p>
                
                <div className="text-sm text-slate-300 space-y-2">
                  <p><strong>If the notes are lighting up too fast:</strong></p>
                  <ul className="list-disc list-inside space-y-1 ml-2">
                    <li>Pick one chord and watch its highlighted notes</li>
                    <li>When that chord comes back around, play one of those notes</li>
                    <li>Gradually add more chords as you get comfortable</li>
{/*                     <li>Stay in the same fretboard area - don't jump around!</li> */}
                  </ul>
                </div>
                
                <div className="border-t border-slate-700 pt-3 mt-3">
                  <p className="text-xs text-slate-400">
                    💡 <strong>Remember:</strong> You don't need to play over every chord change. Pick your moments, hit those chord tones when you can, and let the backing track carry you. Even pros take breaks during solos!
                  </p>
                </div>
                
                {/* Close button */}
                <button
                  onClick={() => setShowChordToneTargetingHint(false)}
                  className="absolute top-2 right-2 text-slate-400 hover:text-white text-sm"
                  aria-label="Close hint"
                >
                  ×
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Timeline Display */}
      <div className="jam-track-timeline">
        <ChordTimeline
          timeline={track.timeline}
          currentChord={currentChord}
          currentTime={currentTime}
          bpm={track.bpm}
          onChordClick={handleChordClick}
        />
      </div>
    </div>
  );
};

export default JamTrack;
